import {Component, inject, OnDestroy, OnInit, ViewChild} from '@angular/core';
import {FormBuilder, FormControl, FormGroup} from '@angular/forms';
import {MatTableDataSource} from '@angular/material/table';
import {MatPaginator, PageEvent} from '@angular/material/paginator';
import {MatSort, Sort} from '@angular/material/sort';
import {ActivatedRoute, Router} from '@angular/router';
import {PagedFilterDTO, PagedFilterDTONew} from '../../../../common/models/PagedFilterDTO';
import {SharedService} from '../../../../common/services/shared-service';
import {buildRoute, PathSegments, PortalType} from '../../../../common/types/portal.enum';
import {ApplicationDTO} from '../../../models/application/ApplicationDTO';
import {skip, take, takeUntil} from 'rxjs/operators';
import {forkJoin, Observable, Subject} from 'rxjs';
import {TableControl} from '../../../../common/models/TableControl';
import {DateUtil} from '../../../../common/util/DateUtil';
import {createPagedFilterDTO, getSortDirection, getStatusColor} from '../../../../common/util/Util';
import {MatIconType} from '../../../../common/types/icon.enum';
import {PagedResponseDTO} from '../../../../common/models/PagedResponseDTO';
import {PagedRequestDTO} from '../../../../common/models/PagedRequestDTO';
import {detailsExpand} from '../../../../common';
import {NameValueDTO} from '../../../../common/models/NameValueDTO';
import {LookupService} from '../../../../common/services/lookup.service';
import {LookupValue} from '../../../../clm/types/lookup-value.enum';
import {OtherRequestBuilder} from '../../../../other/services/other-request-builder';
import {OnboardingAdminPortalService} from '../../../services/onboard-application.service';
import {
  ApplicationFilterCacheService,
  ApplicationFilterKeys,
  ApplicationFilterType
} from '../../../services/application-filter-cache.service';
import {isValidCommaSeparatedNumberList} from '../../../../clm/common/functions/clm.functions';
import {UserAuthorisationService} from '../../../../common/services/user-authorisation.service';
import {BrokerDTO} from '../../../models/broker/BrokerDTO';
import {ReferenceDTO} from '../../../models/application/ReferenceDTO';
import {ReferenceLookupValue} from '../../../../clm/types/reference-lookup-value.enum';
import {ReferenceService} from '../../../services/reference.service';
import {ApplicationUtil} from '../../../../obd/obd-common/utils/ApplicationUtil';
import {ApplicationIndicatorComponent} from '../../dialogs/application-indicator/application-indicator.component';
import {MatDialog} from '@angular/material/dialog';
import {ProductStore} from '../../../../common/store/product.store';
import {Feature} from '../../../../common/types/feature';

enum TableColumns {
  Icons = 'icons',
  ApplicationId = 'applicationId',
  ApplicationDate = 'applicationDate',
  ApplicationReferenceNumber = 'applicationReferenceNumber',
  MembershipNumber = 'membershipNumber',
  PrincipalLastName = 'principalLastName',
  SchemeName = 'schemeName',
  OptionCode = 'optionCode',
  EmployerGroupName = 'employerGroupName',
  IntermediatoryName = 'intermediatoryName',
  StageName = 'stageName',
  StatusName = 'statusName',
}

const ALL_STATUSES = 'All Statuses';

@Component({
  selector: 'greydom-applications',
  templateUrl: './applications.component.html',
  styleUrls: [],
  animations: [detailsExpand]
})
export class ApplicationsComponent implements OnInit, OnDestroy {
  readonly getStatusColor = getStatusColor;

  readonly tableColumns = TableColumns;
  readonly destroyed$: Subject<boolean> = new Subject();
  readonly dateFormat: string = DateUtil.dateFormat;
  readonly dateTimeFormat = DateUtil.dateTimeFormat;
  readonly DisplayIcon = MatIconType;
  readonly APPLICATION_STATE_IN_PROGRESS = 'In Progress';
  readonly STATUS_NOT_TAKEN_UP = {
            applicationStatusId: 5,
            statusName: 'Not Taken Up',
            statusCode: 'NTU'
          };
  readonly SAVE_DATA_STAGE = {
              applicationStageId: 1,
              stageCode: 'SD',
              stageName: 'Save Data'
        };
  readonly PortalType = PortalType;

  currentPortal: PortalType;
  expandedElement: ApplicationDTO | null;
  applicationTableControl: TableControl;
  displayedColumns: TableColumns[] = Object.values(TableColumns);
  dataSource: MatTableDataSource<ApplicationDTO> = new MatTableDataSource();
  directPayingMemberGroups: ReferenceDTO[] = [];

  // Filters:
  searchFormGroup: FormGroup;
  searchValue: string;
  batchReferenceNumberValue: string;
  status: string;
  statusesLookUpValues: NameValueDTO[];

  @ViewChild(MatPaginator) paginator: MatPaginator;
  @ViewChild(MatSort) sort: MatSort;

  private loggedInBrokerCode: string;
  private loggedInBrokerProfile: BrokerDTO;
  loggedInUserFirstLastName = '';
  hasEditingOverridePermissions = false;
  products: string[];

  private readonly productStore = inject(ProductStore);

  constructor(
    private applicationService: OnboardingAdminPortalService,
    private activatedRoute: ActivatedRoute,
    private formBuilder: FormBuilder,
    private lookupService: LookupService,
    private router: Router,
    private sharedService: SharedService,
    private applicationFilterCacheService: ApplicationFilterCacheService,
    private userAuthorisationService: UserAuthorisationService,
    private referenceService: ReferenceService,
    private dialog: MatDialog,
  ) {
    const controls = OtherRequestBuilder.getTableControls();
    this.applicationTableControl = new TableControl(
      controls.length,
      controls.pageSize,
      controls.pageNumber,
      controls.pageSizeOptions,
      'applicationId',
      'DESCENDING',
      '',
      ''
    );

    const parentData = this.activatedRoute.parent?.snapshot.data;
    this.currentPortal = parentData?.portalType || PortalType.Normal;
    this.searchValue = '';
    this.status = ALL_STATUSES;
  }

  ngOnInit(): void {
    // Get lookup values first
    this.getListOfLookupValues();
    this.hasEditingOverridePermissions = this.userAuthorisationService.hasRoleOperationsManager() || this.userAuthorisationService.hasOnBoardingQC();

    this.searchFormGroup = this.formBuilder.group({
      searchApplication: new FormControl(''),
      batchReferenceNumber: new FormControl(''),
    });

    // First get the logged in profile
    const userProfile$ = this.sharedService.getLoggedInUserProfile$().pipe(
      take(1),
      takeUntil(this.destroyed$)
    );

    const brokerProfile$ = this.sharedService.getLoggedInBrokerProfile$().pipe(
      take(1),
      takeUntil(this.destroyed$)
    );

    const filterValues$ = this.applicationFilterCacheService.getFilterValue().pipe(
      take(1),
      takeUntil(this.destroyed$)
    );

    // Initialise
    forkJoin([userProfile$, brokerProfile$, filterValues$]).subscribe(([userProfile, brokerProfile, filterValues]) => {
      if (userProfile) {
        this.loggedInUserFirstLastName = this.sharedService.getLoggedInUserName();
        this.loggedInBrokerCode = this.sharedService.getProfileLinkedEntityCode();
      }

      if (brokerProfile) {
        this.loggedInBrokerProfile = brokerProfile;
      }

      if (filterValues) {
        this.restoreFilterValues(filterValues);
      }

      // Always fetch applications after filter values are set/changed
      this.getListOfApplications(this.applicationTableControl);
    });

    // Subscribe to filter updates AFTER init
    this.applicationFilterCacheService.getFilterValue().pipe(
      skip(1), // ignore the first value already handled in forkJoin
      takeUntil(this.destroyed$)
    ).subscribe(updatedFilters => {
      if (updatedFilters) {
        this.restoreFilterValues(updatedFilters);
        this.getListOfApplications(this.applicationTableControl);
      }
    });
  }

  ngOnDestroy(): void {
    this.destroyed$.next(true);
    this.destroyed$.complete();
  }

   archiveApplication(application: ApplicationDTO): void{
      const dialogRef = this.dialog.open(ApplicationIndicatorComponent, {
            width: '40vw',
            data: {applicationId: application.applicationId, selectedIndicators: application.indicators, createdBy: this.loggedInUserFirstLastName}
          });
      dialogRef.afterClosed().subscribe(indicators => {
        if (indicators){

          this.applicationService.getApplicationById(application.applicationId)
          .pipe(take(1))
          .subscribe({
            next: completeApplication => {
              completeApplication.stage = this.SAVE_DATA_STAGE;
              completeApplication.status = this.STATUS_NOT_TAKEN_UP;
              completeApplication.updated = new Date();
              completeApplication.updatedBy = this.loggedInUserFirstLastName;
              completeApplication.indicators = indicators;

              this.applicationService.updateApplication(completeApplication)
                .pipe(
                  take(1),
                )
                .subscribe({
                  next: (response: ApplicationDTO) => {
                    application.stage = this.SAVE_DATA_STAGE;
                    application.status = this.STATUS_NOT_TAKEN_UP;

                    this.sharedService.openSnackBar('Application updated successfully', 'Dismiss');
                  },
                error: (errorResponse) => {
                    const error = errorResponse.error;
                    this.sharedService.openErrorDialog('Failed to process application');
                  }
                });
                },
                error: error => {
                  console.error('Error fetching application:', error);
                }
              });
        }
        });
    }

  onValueChanged(value: string): void {
    if (this.paginator) {
      this.paginator.firstPage();
    }
    this.applicationTableControl.filterField = TableColumns.ApplicationReferenceNumber;
    this.applicationTableControl.filterValue = value;
    this.applicationTableControl.pageNumber = 0;
    this.applicationFilterCacheService.saveMultipleFilterValues({
      searchValue: value,
      pageNumber: 0
    });
  }

  onBatchReferenceChanged(value: any) {
    this.batchReferenceNumberValue = value;
    this.applicationFilterCacheService.saveFilterValue(ApplicationFilterKeys.BatchReferenceNumber, value);
  }

  onStatusChanged(value: string): void {
    if (this.paginator) {
      this.paginator.firstPage();
    }
    this.status = value;
    this.applicationTableControl.pageNumber = 0;
    this.applicationFilterCacheService.saveMultipleFilterValues({
      status: value,
      pageNumber: 0
    });
  }

  onProductChanged(event: string[]): void {
    if (event?.length == 0) {
      return;
    }

    this.paginator?.firstPage();

    this.applicationTableControl.pageNumber = 0;
    this.applicationFilterCacheService.saveMultipleFilterValues({
      pageNumber: 0,
      products: [...event]
    });
  }

  getListOfApplications(tableControl: TableControl): void {
    const pagedRequestDTO: PagedRequestDTO = this.getPagedRequestDTO(tableControl);

    this.applicationService.getListOfApplications(pagedRequestDTO)
      .pipe(takeUntil(this.destroyed$))
      .subscribe({
        next: (response: PagedResponseDTO<ApplicationDTO>) => {
          this.dataSource.data = response.resultList;
          this.applicationTableControl.length = response.totalRecords;
          this.setPaginatorProperties(this.applicationTableControl.pageSize, this.applicationTableControl.pageNumber);
        },
        error: (error) => console.error('Error fetching applications:', error)
      });
  }

  sortData($event: Sort): void {
    if (!$event.direction) {
      return;
    }
    const sortFieldMap = {
      applicationDate: 'applicationMembershipByApplicationMembershipId.applicationDate',
      applicationReferenceNumber: 'applicationReferenceNumber',
      membershipNumber: 'applicationMembershipByApplicationMembershipId.membershipNumber',
      principalLastName: 'applicationMembershipByApplicationMembershipId.principalLastName',
      schemeName: 'applicationSchemeByApplicationSchemeId.schemeName',
      optionCode: 'applicationMembershipByApplicationMembershipId.optionCode',
      employerGroupName: 'applicationEmployerGroupByApplicationEmployerGroupId.employerGroupName',
      intermediatoryName: 'applicationIntermediatoryByApplicationIntermediatoryId.intermediatoryName',
      stageName: 'applicationStageByApplicationStageId.stageName',
      statusName: 'applicationStatusByApplicationStatusId.statusName'
    };

    if (this.paginator) {
      this.paginator.firstPage();
    }
    this.applicationTableControl.sortField = sortFieldMap[$event.active] || $event.active;
    this.applicationTableControl.sortDirection = getSortDirection($event.direction);
    this.applicationTableControl.pageNumber = 0;
    this.applicationFilterCacheService.saveMultipleFilterValues({
      sortField: this.applicationTableControl.sortField,
      sortDirection: this.applicationTableControl.sortDirection,
      pageNumber: this.applicationTableControl.pageNumber
    });
  }

  pageChanged($event: PageEvent): void {
    this.applicationTableControl.pageNumber = $event.pageIndex;
    this.applicationTableControl.pageSize = $event.pageSize;
    this.applicationFilterCacheService.saveMultipleFilterValues({
      pageNumber: this.applicationTableControl.pageNumber,
      pageSize: this.applicationTableControl.pageSize
    });
  }

  onClickAddDependent(): void {
    this.router.navigate([this.getRouteForAddDependent()]);
  }

  onClickImportApplications(): void {
    this.router.navigate([this.getRouteForImportApplication()]);
  }

  onClickGroupApplication(): void {
    this.router.navigate([this.getRouteForGroupApplication()]);
  }

  onClickIndividualApplication(): void {
    this.router.navigate([this.getRouteForIndividualApplication()]);
  }

  isApplicationInProgressCapturing(application: ApplicationDTO): boolean {
    if (!application?.status) {
      return false;
    }
    return application.status.statusName == this.APPLICATION_STATE_IN_PROGRESS ;
  }

  isInProgressApplicationNotTakenUp(application: ApplicationDTO): boolean {
    if (!application?.status) {
      return false;
    }
    return application.status.statusName == this.STATUS_NOT_TAKEN_UP.statusName && application.stage.stageCode == this.SAVE_DATA_STAGE.stageCode ;
  }

  isApplicationInProgress(application: ApplicationDTO) {
    return this.isApplicationInProgressCapturing(application) || this.isInProgressApplicationNotTakenUp(application);
  }

  isReviewIconEnabled(application: ApplicationDTO): boolean {
    return !this.isApplicationInProgress(application);
  }

  isEditIconEnabled(application: ApplicationDTO): boolean {
    return this.isApplicationInProgress(application) && this.hasPermissionToEditApplication(application);
  }

  isCancelIconEnabled(application: ApplicationDTO): boolean {
    return this.isApplicationInProgressCapturing(application) && this.hasPermissionToEditApplication(application);
  }

  isEditHelpIconEnabled(application: ApplicationDTO): boolean {
    return this.isApplicationInProgress(application) && !this.hasPermissionToEditApplication(application);
  }

  private restoreFilterValues(filterValues: ApplicationFilterType): void {
    if (!filterValues) { return; }
    const {
      searchValue,
      batchReferenceNumber,
      status,
      products,
      pageNumber,
      pageSize,
      sortField,
      sortDirection,
    } = filterValues;

    // Search value
    this.status = status || ALL_STATUSES;
    this.searchValue = searchValue ?? '';
    this.batchReferenceNumberValue = batchReferenceNumber || '';
    if (products?.length > 0) {
      this.products = [...products];
    }

    this.searchFormGroup.patchValue({
      searchApplication: this.searchValue,
      batchReferenceNumber: this.batchReferenceNumberValue
    });

    this.applicationTableControl.filterValue = searchValue || '';

    // Update Table control cached values
    this.applicationTableControl.pageSize = pageSize || this.applicationTableControl.pageSize;
    this.applicationTableControl.pageNumber = pageNumber || this.applicationTableControl.pageNumber;
    this.applicationTableControl.sortField = sortField || this.applicationTableControl.sortField;
    this.applicationTableControl.sortDirection = sortDirection || this.applicationTableControl.sortDirection;
  }

  private getListOfLookupValues(): void {
    const lookupEntities: string[] = [LookupValue.ApplicationStatus];

    const nameValueRequests: Observable<NameValueDTO[]>[] = lookupEntities.map((entity: string) =>
      this.lookupService.getListOfLookupValues(entity)
    );

    forkJoin(nameValueRequests)
      .pipe(take(1))
      .subscribe({
        next: (responses: NameValueDTO[][]) => {
          this.statusesLookUpValues = this.updateLookupWithDefault(responses[0], ALL_STATUSES);
        },
        error: (errorResponse: any) => {
          console.error('Failed to fetch lookup values:', errorResponse);
        }
      });

    this.referenceService.getAllByFieldName(ReferenceLookupValue.DirectPayingMember)
      .pipe(take(1))
      .subscribe({
        next: (results: ReferenceDTO[]) => {
          this.directPayingMemberGroups = results;
        },
      });
  }

  private updateLookupWithDefault(response: NameValueDTO[] | null, defaultName: string): NameValueDTO[] {
    const processedArray: NameValueDTO[] = [...(response || [])];
    processedArray.unshift({value: 0, name: defaultName});
    return processedArray;
  }

  private getPagedRequestDTO(tableControl: TableControl): PagedRequestDTO {
    const pagedRequestDTO: PagedRequestDTO = OtherRequestBuilder.getTblApplicationPagedRequestDTO(tableControl);
    pagedRequestDTO.fieldMap = {};


    if (this.currentPortal === PortalType.Broker && !this.loggedInBrokerCode) {
      return; // Imp!! Don't fetch applications other than that broker
    }
    // Add broker filter for broker portal to only show applications for current broker
    if (this.currentPortal === PortalType.Broker) {
      const loggedInAsBrokerGroup = this.loggedInBrokerProfile != null
        && this.loggedInBrokerProfile.businessType?.value == 7
        && this.loggedInBrokerProfile.brokerAgents != null
        && this.loggedInBrokerProfile.brokerAgents.length > 0;

      if (loggedInAsBrokerGroup) {
        const brokerFilterGroup = new PagedFilterDTONew();
        brokerFilterGroup.name = 'applicationIntermediatoryByApplicationIntermediatoryId.intermediatoryCode';
        brokerFilterGroup.operator = 'in';
        brokerFilterGroup.value = this.loggedInBrokerProfile.brokerAgents.map(agent => agent.code).join(','); // Concatenate all broker agents' codes
        brokerFilterGroup.bracket = '(';
        pagedRequestDTO.fieldMap['applicationIntermediatoryByApplicationIntermediatoryId_2'] = brokerFilterGroup;
      }

      const brokerFilter = new PagedFilterDTONew();
      brokerFilter.name = 'applicationIntermediatoryByApplicationIntermediatoryId.intermediatoryCode';
      brokerFilter.operator = '==';
      brokerFilter.value = this.loggedInBrokerCode; // current broker username
      if (loggedInAsBrokerGroup) {
        brokerFilter.logicGate = 'OR';
        brokerFilter.bracket = ')';
      }
      pagedRequestDTO.fieldMap['applicationIntermediatoryByApplicationIntermediatoryId'] = brokerFilter;
    }

    const productsFilter = this.productStore.buildProductFilter(
      this.products,
      'applicationMembershipByApplicationMembershipId.optionCode'
    );
    if (productsFilter) {
      pagedRequestDTO.fieldMap['applicationMembershipByApplicationMembershipId'] = productsFilter;
    }

    if (this.batchReferenceNumberValue) {
      const batchReferenceFilter = new PagedFilterDTO();
      batchReferenceFilter.name = 'batchReferenceNumber';
      batchReferenceFilter.value = this.batchReferenceNumberValue;
      pagedRequestDTO.fieldMap['batchReferenceNumber'] = batchReferenceFilter;
    }

    // Add search filters if search value exists
    if (tableControl?.filterValue?.trim()) {
      const searchValue = tableControl.filterValue.trim();
      const isNumber = isValidCommaSeparatedNumberList(searchValue);

      // Define searchable fields with their properties
      const searchFields = [
        { key: 'applicationReferenceNumber', name: 'applicationReferenceNumber', logicGate: 'AND', bracket: '(' },
        // Only include applicationId search if value is a valid number
        ...(isNumber ? [{ key: 'applicationId', name: 'applicationId', logicGate: 'OR' }] : []),
        { key: 'applicationMembershipByApplicationMembershipId_1', name: 'applicationMembershipByApplicationMembershipId.membershipNumber', logicGate: 'OR' },
        { key: 'applicationMembershipByApplicationMembershipId_2', name: 'applicationMembershipByApplicationMembershipId.principalLastName', logicGate: 'OR' },
        { key: 'applicationMembershipByApplicationMembershipId_3', name: 'applicationMembershipByApplicationMembershipId.optionCode', logicGate: 'OR' },
        { key: 'applicationMembershipByApplicationMembershipId_4', name: 'applicationMembershipByApplicationMembershipId.principalIdNumber', logicGate: 'OR'},
        { key: 'applicationIntermediatoryByApplicationIntermediatoryId_1', name: 'applicationIntermediatoryByApplicationIntermediatoryId.intermediatoryCode', logicGate: 'OR' },
        { key: 'applicationEmployerGroupByApplicationEmployerGroupId', name: 'applicationEmployerGroupByApplicationEmployerGroupId.employerGroupCode', logicGate: 'OR', bracket: ')' }
      ];

      // Add each search field to the request
      searchFields.forEach(field => {
        if (this.currentPortal === PortalType.Broker && field.key.startsWith('applicationIntermediatoryByApplicationIntermediatoryId')) {
          // Skip broker filter for intermediary code in Broker portal
          return;
        }
        pagedRequestDTO.fieldMap[field.key] = {
          name: field.name,
          value: searchValue,
          logicGate: field.logicGate,
          bracket: field.bracket || ''
        };
      });
    }

    if (this.status !== ALL_STATUSES) {
      pagedRequestDTO.fieldMap[`applicationStatusByApplicationStatusId`] = createPagedFilterDTO('applicationStatusByApplicationStatusId.statusName', this.status);
    }

    return pagedRequestDTO;
  }
  private setPaginatorProperties(pageSize: number, pageIndex: number): void {
    if (this.paginator) {
      this.paginator.pageIndex = pageIndex;
      this.paginator.pageSize = pageSize;
    }
  }

  getRouteForImportApplication(): string {
    const portal = this.currentPortal || PortalType.Normal;
    return buildRoute(portal, PathSegments.ImportApplication);
  }

  getRouteForEditApplication(groupCode: string): string {
    const isIndividualApplication = ApplicationUtil.isInEmployerGroupList(this.directPayingMemberGroups, groupCode);
    return isIndividualApplication ? this.getRouteForIndividualApplication() : this.getRouteForGroupApplication();
  }

  getRouteForGroupApplication(): string {
    const routes = {
      [PortalType.Normal]: buildRoute(PortalType.Normal, PathSegments.GroupApplication),
      [PortalType.OnBoarding]: buildRoute(PortalType.OnBoarding, PathSegments.GroupApplication),
      [PortalType.Broker]: buildRoute(PortalType.Broker, PathSegments.GroupApplication)
    };
    return routes[this.currentPortal] || routes[PortalType.Normal];
  }

  getRouteForIndividualApplication(): string {
    const routes = {
      [PortalType.Normal]: buildRoute(PortalType.Normal, PathSegments.IndividualApplication),
      [PortalType.OnBoarding]: buildRoute(PortalType.OnBoarding, PathSegments.IndividualApplication),
      [PortalType.Broker]: buildRoute(PortalType.Broker, PathSegments.IndividualApplication)
    };
    return routes[this.currentPortal] || routes[PortalType.Normal];
  }

  getRouteForAddDependent(): string {
    const routes = {
      [PortalType.Normal]: buildRoute(PortalType.Normal, PathSegments.AddPerson),
      [PortalType.OnBoarding]: buildRoute(PortalType.OnBoarding, PathSegments.AddPerson),
      [PortalType.Broker]: buildRoute(PortalType.Broker, PathSegments.AddPerson)
    };
    return routes[this.currentPortal] || routes[PortalType.Normal];
  }

  getRouteForReview(applicationId: number): string {
    const routes = {
      [PortalType.Normal]: `${buildRoute(PortalType.Normal, PathSegments.ReviewApplication)}/${applicationId}`,
      [PortalType.OnBoarding]: `${buildRoute(PortalType.OnBoarding, PathSegments.ReviewApplication)}/${applicationId}`,
      [PortalType.Broker]: `${buildRoute(PortalType.Broker, PathSegments.ReviewApplication)}/${applicationId}`
    };
    return routes[this.currentPortal] || routes[PortalType.Normal];
  }

  private hasPermissionToEditApplication(application: ApplicationDTO) {
    const originalCapturer = this.loggedInUserFirstLastName?.toUpperCase() === application.createdBy?.toUpperCase();
    return originalCapturer || this.hasEditingOverridePermissions;
  }
}
